/**
 * "Choose sessions" for one connected AI tool (TMMAPP-0142 L1): the person's active assistant
 * sessions (`GET /v1/me/assistant/sessions?status=ACTIVE`; never Help & feedback, which can't be
 * granted), each with Off / Read only / Read and write as the connection holds it. Each choice saves
 * at once, so the dialog's one action is Done.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AgentAccessChoice, AgentConnection } from '@/api/agentConnections';
import { assistantApi, type AssistantSession } from '@/api/assistant';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { ErrorState } from '@/components/ErrorState';
import { AgentAccessControl } from './AgentAccessControl';

const TITLE_ID = 'agent-tools-choose-title';

interface ChooseSessionsDialogProps {
  connection: AgentConnection;
  onChoose: (sessionId: number, choice: AgentAccessChoice) => Promise<void>;
  onClose: () => void;
}

export function ChooseSessionsDialog({ connection, onChoose, onClose }: ChooseSessionsDialogProps) {
  const { t } = useTranslation();
  const openSubscribe = useOpenSubscribe();
  // Read and write for a GUEST or NONE tier: this dialog closes, then the purchase opens (DLG-005).
  const purchase = () => {
    onClose();
    openSubscribe();
  };
  const [sessions, setSessions] = useState<AssistantSession[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    assistantApi.list('ACTIVE').then(
      (rows) => {
        if (!live) return;
        setSessions(rows.filter((row) => row.kind !== 'SUPPORT'));
        setLoadError(null);
      },
      (error: unknown) => {
        if (live) setLoadError(error);
      },
    );
    return () => {
      live = false;
    };
  }, [attempt]);

  const accessOf = (sessionId: number): AgentAccessChoice =>
    connection.grants.find((grant) => grant.sessionId === sessionId)?.access ?? 'OFF';

  return (
    <Dialog open onClose={onClose} aria-labelledby={TITLE_ID} fullWidth maxWidth="sm">
      <DialogTitle id={TITLE_ID}>
        {t('agentTools.pickerTitle', { label: connection.label })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('agentTools.pickerIntro')}</DialogContentText>
        {sessions === null && loadError !== null && (
          <ErrorState
            error={loadError}
            what={t('agentTools.pickerLoadError')}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        )}
        {sessions === null && loadError === null && (
          <Typography variant="body2">{t('common.loading')}</Typography>
        )}
        {sessions !== null && sessions.length === 0 && (
          <Typography variant="body2" data-agent-picker-empty="">
            {t('agentTools.pickerEmpty')}
          </Typography>
        )}
        {sessions !== null && sessions.length > 0 && (
          <Stack spacing={2} data-agent-picker="">
            {sessions.map((session) => (
              <Stack key={session.id} sx={{ gap: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                  {session.name}
                </Typography>
                <AgentAccessControl
                  label={t('agentTools.accessFor', { session: session.name })}
                  value={accessOf(session.id)}
                  onChoose={(choice) => onChoose(session.id, choice)}
                  onPurchase={purchase}
                />
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
}
